import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Check, Chrome, Copy, Download, Loader2, RefreshCw, Smartphone } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { PageContainer } from '../../../common/components/PageContainer';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { TeamDevice, useTeamDevicesApi } from '../../../common/hooks/use-team-devices-api.hook';
import { useStaffName } from '../../../modules/support/staff/staff-support.store';
import {
  ExtensionStatus, connectExtension, disconnectExtension, pingExtension,
} from '../../../modules/superadmin/team-devices/team-extension.bridge';

/**
 * Why the API turned this browser's key down (internal-traffic whoami):
 * `missing` — no key on the request (the extension's rule isn't in place);
 * `invalid` — signed by another environment's API (local, dev, production each have their own);
 * `revoked` — the device was revoked or no longer exists.
 */
type Rejection = 'missing' | 'invalid' | 'revoked' | 'unreachable';

const REJECTION_KEY: Record<Rejection, string> = {
  missing: 'TEAM.NOT_VERIFIED',
  invalid: 'TEAM.NOT_VERIFIED_OTHER_ENV',
  revoked: 'TEAM.NOT_VERIFIED_REVOKED',
  unreachable: 'TEAM.NOT_VERIFIED_UNREACHABLE',
};

/** The API's view of this browser (see internal-traffic whoami). Plain fetch: no sign-in involved. */
async function whoAmI(): Promise<{ recognised: boolean; reason?: string } | null> {
  const base = (process.env.REACT_APP_PMS_API_URL || '').replace(/\/+$/, '');
  const res = await fetch(`${base}/services/pms/internal-traffic/whoami`);
  return res.ok ? res.json() : null;
}

dayjs.extend(relativeTime);

export const EXTENSION_ZIP = '/downloads/ardealy-team-extension.zip';
/** Written next to the zip by `npm run extension:zip`: the version it holds, and its settings. */
export const EXTENSION_INFO = '/downloads/ardealy-team-extension.json';
/**
 * The version this build shipped with, for when the info file can't be read.
 * Before 1.1.0 the extension had no host access to the backoffice, so Chrome
 * left the key off this page's own requests: the check here said "missing"
 * although visits to visualizers were marked. Before 1.2.0 it had none to the
 * demo sites (*.ardealy.link): a teammate opening a demo counted as the prospect.
 */
export const LATEST_EXTENSION_VERSION = '1.2.0';

/** -1, 0 or 1, comparing dotted versions number by number. */
export const compareVersions = (a: string, b: string): number => {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) < (pb[i] || 0) ? -1 : 1;
  }
  return 0;
};
/** Red outline on the outline variant: the default variant is filled and would win the text colour. */
const DESTRUCTIVE = 'border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive';

function browserLabel(name: string): string {
  const ua = navigator.userAgent;
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : 'computer';
  return `${name} — Chrome on ${os}`;
}

/**
 * The team's devices. Visits from them are our own testing: the API keeps them
 * apart (internal_events) so they never reach a vendor's numbers. A laptop gets
 * the Ardealy Team extension and is connected from here in one click; a phone
 * gets a link that marks the browser it is opened in.
 */
export const TeamDevicesPage = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const api = useTeamDevicesApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const nameOf = useStaffName();
  const me = user?.email;
  const firstName = user?.firstname || (me || '').split('@')[0];

  const [devices, setDevices] = useState<TeamDevice[] | null>(null);
  const [ext, setExt] = useState<ExtensionStatus | null | 'checking'>('checking');
  const [browserName, setBrowserName] = useState(() => browserLabel(firstName));
  const [phoneName, setPhoneName] = useState(() => t('TEAM.PHONE_LABEL_DEFAULT', { name: firstName }));
  const [phoneLink, setPhoneLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [revoking, setRevoking] = useState<TeamDevice | null>(null);
  /** What the API makes of this browser's requests: the proof the extension works here. */
  const [verified, setVerified] = useState<boolean | null>(null);
  const [rejection, setRejection] = useState<Rejection | null>(null);
  // Loaders must not depend on `t`: a new `t` would re-run them on every render.
  const tRef = useRef(t);
  tRef.current = t;

  const load = useCallback(async () => {
    try {
      const list = await apiRef.current.list();
      if (list) setDevices(list);
    } catch (e: any) {
      toast.error(e?.message || tRef.current('TEAM.FAILED'));
    }
  }, []);

  const check = useCallback(async () => {
    setExt('checking');
    setExt(await pingExtension());
  }, []);

  useEffect(() => {
    load();
    check();
  }, [load, check]);

  // The version the download holds — the same file the extension checks every hour.
  const [latestVersion, setLatestVersion] = useState(LATEST_EXTENSION_VERSION);
  useEffect(() => {
    let cancelled = false;
    fetch(EXTENSION_INFO, { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((info) => {
        if (!cancelled && typeof info?.version === 'string') setLatestVersion(info.version);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const status = ext === 'checking' ? null : ext;
  const outdated = !!status?.version && compareVersions(status.version, latestVersion) < 0;
  const connectedId = status?.connected ? status.device?.id : undefined;

  // Once connected, ask the API whether it sees this browser's key (it rides on
  // every call to the API, this one included). Also records the device as seen.
  useEffect(() => {
    if (!connectedId) {
      setVerified(null);
      setRejection(null);
      return;
    }
    let cancelled = false;
    // One retry: an extension updated or reloaded a moment ago may still be installing its rule.
    const ask = (retry: boolean) =>
      whoAmI()
        .then((who) => {
          if (cancelled) return;
          if (!who?.recognised && retry) return void setTimeout(() => ask(false), 1500);
          setVerified(!!who?.recognised);
          setRejection(who?.recognised ? null : who && who.reason in REJECTION_KEY ? (who.reason as Rejection) : who ? 'missing' : 'unreachable');
          load();
        })
        .catch(() => {
          if (cancelled) return;
          setVerified(false);
          setRejection('unreachable');
        });
    ask(true);
    return () => {
      cancelled = true;
    };
  }, [connectedId, load]);
  const connectedDevice = status?.connected && status.device ? devices?.find((d) => d._id === status.device!.id) : undefined;
  // Connected with a key that has since been revoked (from another browser, or by a teammate).
  const stale = !!(status?.connected && devices && (!connectedDevice || connectedDevice.revokedAt));

  const connect = async () => {
    setBusy(true);
    let created: TeamDevice | null = null;
    try {
      // Ask again first: an extension reloaded or updated since this page loaded
      // no longer answers here until the page is reloaded. Better to say so than
      // to make a device whose key never reaches the browser.
      const before = await pingExtension();
      if (!before || before.error) {
        setExt(before);
        toast.error(t('TEAM.RELOAD'));
        return;
      }
      const { device, key } = await api.create({ label: browserName.trim() || browserLabel(firstName), kind: 'extension' });
      created = device;
      const result = await connectExtension(key, { id: device._id, label: device.label, owner: device.owner });
      if (!result?.connected || result.error) throw new Error(t('TEAM.RELOAD'));
      created = null;
      setExt(result);
      await load();
    } catch (e: any) {
      // The key never reached the extension: take the device back rather than leave one that can never be used.
      if (created) await api.revoke(created._id).catch(() => undefined);
      toast.error(e?.message || t('TEAM.FAILED'));
      await load();
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      // A key from another environment may be the one that works there (production's,
      // seen from a local backoffice): take it off this browser, but don't revoke it.
      if (connectedDevice && !connectedDevice.revokedAt && rejection !== 'invalid') await api.revoke(connectedDevice._id);
      setExt(await disconnectExtension());
      await load();
    } catch (e: any) {
      toast.error(e?.message || t('TEAM.FAILED'));
    } finally {
      setBusy(false);
    }
  };

  const createPhoneLink = async () => {
    setBusy(true);
    try {
      const { key } = await api.create({ label: phoneName.trim() || t('TEAM.PHONE_LABEL_DEFAULT', { name: firstName }), kind: 'phone' });
      const base = (process.env.REACT_APP_VISUALIZER_URL || '').replace(/\/+$/, '');
      setPhoneLink(`${base}/team-device?ardealy_team=${encodeURIComponent(key)}`);
      setCopied(false);
      await load();
    } catch (e: any) {
      toast.error(e?.message || t('TEAM.FAILED'));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!phoneLink) return;
    try {
      await navigator.clipboard.writeText(phoneLink);
      setCopied(true);
    } catch {
      // The link stays selectable on screen.
    }
  };

  const revoke = async () => {
    if (!revoking) return;
    setBusy(true);
    try {
      await api.revoke(revoking._id);
      if (status?.device?.id === revoking._id) setExt(await disconnectExtension());
      setRevoking(null);
      await load();
    } catch (e: any) {
      toast.error(e?.message || t('TEAM.FAILED'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageContainer breadcrumbs={[{ label: t('TEAM.TITLE') }]}>
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="mb-1 font-heading text-2xl font-medium tracking-tight">{t('TEAM.TITLE')}</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">{t('TEAM.SUBTITLE')}</p>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* This browser: the extension */}
          <Card className="gap-4 p-5" data-testid="team-this-browser">
            <div className="flex items-center gap-2">
              <Chrome className="size-4 text-muted-foreground" />
              <h2 className="text-sm font-medium">{t('TEAM.THIS_BROWSER')}</h2>
              {status && <span className="ms-auto text-xs text-muted-foreground">{t('TEAM.VERSION', { version: status.version })}</span>}
            </div>

            {ext === 'checking' ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                {t('TEAM.CHECKING')}
              </p>
            ) : !status ? (
              <div className="flex flex-col gap-3" data-testid="team-not-installed">
                <p className="text-sm">{t('TEAM.NOT_INSTALLED')}</p>
                <ol className="list-decimal space-y-1.5 ps-5 text-sm text-muted-foreground">
                  <li>{t('TEAM.STEP_DOWNLOAD')}</li>
                  <li>{t('TEAM.STEP_LOAD')}</li>
                  <li>{t('TEAM.STEP_INCOGNITO')}</li>
                </ol>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" asChild>
                    <a href={EXTENSION_ZIP} download>
                      <Download data-icon="inline-start" />
                      {t('TEAM.DOWNLOAD')}
                    </a>
                  </Button>
                  <Button variant="ghost" onClick={check}>
                    <RefreshCw data-icon="inline-start" />
                    {t('TEAM.CHECK_AGAIN')}
                  </Button>
                </div>
              </div>
            ) : status.connected && !stale ? (
              <div className="flex flex-col gap-3" data-testid="team-connected">
                <p className="flex items-center gap-2 text-sm font-medium">
                  <span className="size-2 rounded-full bg-success" />
                  {t('TEAM.CONNECTED')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {status.device?.owner && status.device.owner !== me
                    ? `${status.device.label} · ${nameOf(status.device.owner)}`
                    : status.device?.label}
                </p>
                {/* An outdated extension can't be checked from here: say that, not "no key". */}
                {verified !== null && !(outdated && !verified && rejection === 'missing') && (
                  <p className={cn('text-xs', verified ? 'text-success' : 'text-destructive')} data-testid="team-verified">
                    {verified ? t('TEAM.VERIFIED') : t(REJECTION_KEY[rejection || 'missing'])}
                  </p>
                )}
                {outdated && (
                  <div className="flex flex-col gap-2 rounded-lg bg-muted px-3 py-2.5" data-testid="team-outdated">
                    <p className="text-xs text-muted-foreground">
                      {t('TEAM.OUTDATED', { version: latestVersion })}
                    </p>
                    <div>
                      <Button variant="outline" size="sm" asChild>
                        <a href={EXTENSION_ZIP} download>
                          <Download data-icon="inline-start" />
                          {t('TEAM.DOWNLOAD_UPDATE', { version: latestVersion })}
                        </a>
                      </Button>
                    </div>
                  </div>
                )}
                <div>
                  <Button variant="outline" className={DESTRUCTIVE} disabled={busy} onClick={disconnect}>
                    {t('TEAM.DISCONNECT')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3" data-testid="team-not-connected">
                <p className="text-sm">{status.error ? t('TEAM.RELOAD') : stale ? t('TEAM.STALE') : t('TEAM.NOT_CONNECTED')}</p>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="team-browser-name" className="text-xs text-muted-foreground">{t('TEAM.LABEL')}</Label>
                  <Input id="team-browser-name" value={browserName} maxLength={80} onChange={(e) => setBrowserName(e.target.value)} />
                </div>
                <div>
                  <Button variant="outline" disabled={busy} onClick={connect}>
                    {busy ? <Loader2 data-icon="inline-start" className="animate-spin" /> : <Check data-icon="inline-start" />}
                    {t('TEAM.CONNECT')}
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* A phone: a link */}
          <Card className="gap-4 p-5" data-testid="team-phone">
            <div className="flex items-center gap-2">
              <Smartphone className="size-4 text-muted-foreground" />
              <h2 className="text-sm font-medium">{t('TEAM.PHONE')}</h2>
            </div>
            <p className="text-sm text-muted-foreground">{t('TEAM.PHONE_HINT')}</p>
            {phoneLink ? (
              <div className="flex flex-col gap-2">
                <p className="text-sm">{t('TEAM.LINK_READY')}</p>
                <Input readOnly value={phoneLink} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" data-testid="team-phone-link" />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={copyLink}>
                    {copied ? <Check data-icon="inline-start" /> : <Copy data-icon="inline-start" />}
                    {copied ? t('TEAM.COPIED') : t('TEAM.COPY')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="team-phone-name" className="text-xs text-muted-foreground">{t('TEAM.LABEL')}</Label>
                  <Input id="team-phone-name" value={phoneName} maxLength={80} onChange={(e) => setPhoneName(e.target.value)} />
                </div>
                <div>
                  <Button variant="outline" disabled={busy} onClick={createPhoneLink}>
                    {t('TEAM.CREATE_LINK')}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Every device */}
        <div className="flex flex-col gap-3">
          <h2 className="text-sm font-medium">{t('TEAM.ALL')}</h2>
          <Card className="py-0">
            {!devices ? (
              <div className="flex justify-center py-10 text-muted-foreground"><Loader2 className="size-5 animate-spin" /></div>
            ) : devices.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">{t('TEAM.EMPTY')}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="ps-4">{t('TEAM.COL_OWNER')}</TableHead>
                    <TableHead>{t('TEAM.COL_DEVICE')}</TableHead>
                    <TableHead>{t('TEAM.COL_KIND')}</TableHead>
                    <TableHead>{t('TEAM.COL_ADDED')}</TableHead>
                    <TableHead>{t('TEAM.COL_SEEN')}</TableHead>
                    <TableHead className="pe-4 text-end" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices.map((d) => (
                    <TableRow key={d._id} className={cn(d.revokedAt && 'text-muted-foreground')} data-testid="team-device-row">
                      <TableCell className="ps-4">
                        {nameOf(d.owner)}
                        {d.owner === me && <span className="ms-1 text-muted-foreground">{t('TEAM.YOU')}</span>}
                      </TableCell>
                      <TableCell>
                        {d.label}
                        {status?.device?.id === d._id && !d.revokedAt && (
                          <Badge variant="outline" className="ms-2 text-success">{t('TEAM.THIS_BROWSER')}</Badge>
                        )}
                      </TableCell>
                      <TableCell>{d.kind === 'phone' ? t('TEAM.KIND_PHONE') : t('TEAM.KIND_EXTENSION')}</TableCell>
                      <TableCell>{d.createdAt ? dayjs(d.createdAt).format('D MMM YYYY') : '—'}</TableCell>
                      <TableCell>{d.lastSeenAt ? dayjs(d.lastSeenAt).fromNow() : t('TEAM.NEVER')}</TableCell>
                      <TableCell className="pe-4 text-end">
                        {d.revokedAt ? (
                          <span className="text-xs">{t('TEAM.REVOKED', { date: dayjs(d.revokedAt).format('D MMM YYYY') })}</span>
                        ) : (
                          <Button variant="outline" size="sm" className={DESTRUCTIVE} onClick={() => setRevoking(d)}>
                            {t('TEAM.REVOKE')}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>
      </div>

      <AlertDialog open={!!revoking} onOpenChange={(o) => !o && !busy && setRevoking(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('TEAM.REVOKE_TITLE', { label: revoking?.label })}</AlertDialogTitle>
            <AlertDialogDescription>{t('TEAM.REVOKE_BODY')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{t('TEAM.CANCEL')}</AlertDialogCancel>
            <AlertDialogAction variant="outline" className={DESTRUCTIVE} disabled={busy} onClick={(e) => { e.preventDefault(); revoke(); }}>
              {t('TEAM.REVOKE')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default TeamDevicesPage;
